import React, { useState, useEffect } from 'react';
import { BusinessCard, Tag } from '../types';

interface BusinessCardFormProps {
  card?: BusinessCard;
  onSave: (cardData: Partial<BusinessCard>) => void;
  onCancel: () => void;
  loading?: boolean;
  allCards?: BusinessCard[];
}

const BusinessCardForm: React.FC<BusinessCardFormProps> = ({
  card,
  onSave,
  onCancel,
  loading = false,
  allCards = []
}) => {
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    title: '',
    email: '',
    phone: '',
    mobile: '',
    fax: '',
    website: '',
    address: '',
    memo: '',
    tags: [] as string[]
  });

  const [tagInput, setTagInput] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [selectedFrontFile, setSelectedFrontFile] = useState<File | null>(null);
  const [selectedBackFile, setSelectedBackFile] = useState<File | null>(null);
  const [frontImagePreview, setFrontImagePreview] = useState<string | null>(null);
  const [backImagePreview, setBackImagePreview] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [frontRotation, setFrontRotation] = useState(90);
  const [backRotation, setBackRotation] = useState(90);

  useEffect(() => {
    if (card) {
      setFormData({
        full_name: card.full_name || '',
        company: card.company || '',
        title: card.title || '',
        email: card.email || '',
        phone: card.phone || '',
        mobile: card.mobile || '',
        fax: card.fax || '',
        website: card.website || '',
        address: card.address || '',
        memo: card.memo || '',
        tags: card.tags ? card.tags.map(tag => typeof tag === 'string' ? tag : tag.name) : []
      });
      // 既存の画像がある場合はプレビューに設定
      if (card.image_path) {
        setFrontImagePreview(`/media/${card.image_path}`);
      }
      if (card.image_back_path) {
        setBackImagePreview(`/media/${card.image_back_path}`);
      }
    }
  }, [card]);

  // 利用可能なタグを抽出
  useEffect(() => {
    const usedTagsSet = new Set<string>();
    allCards.forEach(card => {
      if (card.tags && Array.isArray(card.tags)) {
        card.tags.forEach(tag => {
          const tagName = typeof tag === 'string' ? tag : (tag?.name || '');
          if (tagName) {
            usedTagsSet.add(tagName);
          }
        });
      }
    });
    setAvailableTags(Array.from(usedTagsSet));
  }, [allCards]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form submission data:', formData);

    // ファイルアップロードがある場合
    if (selectedFrontFile || selectedBackFile) {
      const formDataWithFile = new FormData();
      // バックエンドで期待するフィールドのみ送信（空文字列も明示的に送信）
      const allowedFields = ['full_name', 'company', 'title', 'email', 'phone', 'mobile', 'fax', 'website', 'address', 'memo', 'tags'];
      Object.entries(formData).forEach(([key, value]) => {
        if (allowedFields.includes(key)) {
          if (key === 'tags') {
            formDataWithFile.append('tag_names', (value as string[]).join(','));
          } else {
            // 空文字列も明示的に送信
            formDataWithFile.append(key, String(value || ''));
          }
        }
      });
      if (selectedFrontFile) {
        formDataWithFile.append('image', selectedFrontFile);
      }
      if (selectedBackFile) {
        formDataWithFile.append('image_back', selectedBackFile);
      }
      console.log('Sending FormData with file');
      onSave(formDataWithFile as any);
    } else {
      // JSON送信の場合も同様にフィルタリング（空文字列も明示的に送信）
      const filteredData = {
        full_name: formData.full_name,
        company: formData.company || '',
        title: formData.title || '',
        email: formData.email || '',
        phone: formData.phone || '',
        mobile: formData.mobile || '',
        fax: formData.fax || '',
        website: formData.website || '',
        address: formData.address || '',
        memo: formData.memo || '',
        tag_names: formData.tags
      };
      console.log('Sending filtered JSON data:', filteredData);
      onSave(filteredData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddTag = (tagToAdd?: string) => {
    const newTag = tagToAdd || tagInput.trim();
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag]
      });
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTagInput(value);
    setShowTagSuggestions(value.length > 0 && availableTags.length > 0);
  };

  const filteredTagSuggestions = availableTags.filter(tag =>
    tag.toLowerCase().includes(tagInput.toLowerCase()) &&
    !formData.tags.includes(tag)
  );

  const handleFrontFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFrontFile(file);

      // プレビュー表示
      const reader = new FileReader();
      reader.onload = () => {
        setFrontImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedBackFile(file);

      // プレビュー表示
      const reader = new FileReader();
      reader.onload = () => {
        setBackImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAIAnalysis = async () => {
    setAnalyzing(true);
    try {
      let formData = new FormData();

      if (selectedFrontFile) {
        // 新しくアップロードされたファイルを使用
        formData.append('file', selectedFrontFile);
      } else if (frontImagePreview && card?.image_path) {
        // 既存の画像URLを使用してAI解析
        const imageResponse = await fetch(frontImagePreview);
        const imageBlob = await imageResponse.blob();
        formData.append('file', imageBlob, 'business_card.jpg');
      } else {
        return;
      }

      const response = await fetch('/api/bizcard/v1/ocr/analyze', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bizcard_access_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🔥 WEB AI解析結果:', JSON.stringify(result, null, 2));

        // レスポンス構造の確認とデータ抽出
        const analysisData = result.data || result;
        console.log('🔥 解析データ:', JSON.stringify(analysisData, null, 2));

        // AI解析結果をフォームに反映
        setFormData(prev => ({
          ...prev,
          company: analysisData.company || prev.company,
          full_name: analysisData.full_name || prev.full_name,
          title: analysisData.title || prev.title,
          email: analysisData.email || prev.email,
          phone: analysisData.phone || prev.phone,
          mobile: analysisData.mobile || prev.mobile,
          fax: analysisData.fax || prev.fax,
          website: analysisData.website || prev.website,
          address: analysisData.address || prev.address
        }));
        console.log('🔥 フォームデータ更新後:', {
          mobile: analysisData.mobile,
          fax: analysisData.fax,
          website: analysisData.website,
          company: analysisData.company,
          full_name: analysisData.full_name
        });
      } else {
        console.error('🔥 AI解析レスポンスエラー:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('🔥 エラー詳細:', errorText);
      }
    } catch (error) {
      console.error('AI解析エラー:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const rotateFrontImage = () => {
    setFrontRotation(prev => (prev + 90) % 360);
  };

  const rotateBackImage = () => {
    setBackRotation(prev => (prev + 90) % 360);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '25px', color: '#333', textAlign: 'center' }}>
          {card ? '名刺を編集' : '新しい名刺を登録'}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* 画像アップロード・AI解析セクション */}
          <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px dashed #ddd' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#555', fontSize: '16px' }}>📷 名刺画像アップロード</h3>
              {(selectedFrontFile || frontImagePreview) && (
                <button
                  type="button"
                  onClick={handleAIAnalysis}
                  disabled={analyzing}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: analyzing ? '#ccc' : '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: analyzing ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  {analyzing ? '🤖 解析中...' : '🤖 AI解析'}
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* 表面画像 */}
              <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>表面</h4>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFrontFileSelect}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    marginBottom: '10px'
                  }}
                />
                {frontImagePreview && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', height: '160px', alignItems: 'center', marginBottom: '10px' }}>
                      <img
                        src={frontImagePreview}
                        alt="表面プレビュー"
                        style={{
                          width: '120px',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          transform: `rotate(${frontRotation}deg)`
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={rotateFrontImage}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      🔄 回転
                    </button>
                  </div>
                )}
              </div>

              {/* 裏面画像 */}
              <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>裏面 (任意)</h4>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBackFileSelect}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    marginBottom: '10px'
                  }}
                />
                {backImagePreview && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', height: '160px', alignItems: 'center', marginBottom: '10px' }}>
                      <img
                        src={backImagePreview}
                        alt="裏面プレビュー"
                        style={{
                          width: '120px',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          transform: `rotate(${backRotation}deg)`
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={rotateBackImage}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      🔄 回転
                    </button>
                  </div>
                )}
              </div>
            </div>

            <p style={{ margin: '15px 0 0 0', fontSize: '12px', color: '#666' }}>
              💡 表面画像をアップロード後、「AI解析」ボタンで自動入力できます
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                会社名
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                氏名 <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              役職
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                メールアドレス
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                電話番号
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                携帯電話
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                FAX番号
              </label>
              <input
                type="tel"
                name="fax"
                value={formData.fax}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              ウェブサイト
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://example.com"
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              住所
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>


          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              タグ
            </label>

            {/* 既存タグから選択 */}
            {availableTags.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 5px 0' }}>🏷️ 使用済みタグから選択:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px' }}>
                  {availableTags.filter(tag => !formData.tags.includes(tag)).map((tag, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleAddTag(tag)}
                      style={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '12px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: '#495057',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#e9ecef';
                        e.currentTarget.style.borderColor = '#adb5bd';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#dee2e6';
                      }}
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 新規タグ入力 */}
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyPress={handleTagKeyPress}
                  onFocus={() => setShowTagSuggestions(tagInput.length > 0 && availableTags.length > 0)}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                  placeholder="新しいタグを入力してEnter"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleAddTag()}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  追加
                </button>
              </div>

              {/* タグサジェスション */}
              {showTagSuggestions && filteredTagSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: '80px',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  {filteredTagSuggestions.map((tag, index) => (
                    <div
                      key={index}
                      onClick={() => handleAddTag(tag)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: index < filteredTagSuggestions.length - 1 ? '1px solid #eee' : 'none',
                        fontSize: '14px'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 選択されたタグ表示 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    gap: '4px'
                  }}
                >
                  {typeof tag === 'string' ? tag : (tag as Tag).name}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(typeof tag === 'string' ? tag : (tag as Tag).name)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#1976d2',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: 0
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              メモ
            </label>
            <textarea
              name="memo"
              value={formData.memo}
              onChange={handleChange}
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '12px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 20px',
                backgroundColor: loading ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {loading ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessCardForm;